import * as React from "react";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";

import { ITableInstance } from "src/library/components/Tables/Table";
import Grid from "src/library/components/Hedron";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import {
  ISupportTicket,
  useSupportApi
} from "src/modules/support/api/support.api";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import { getCustomerSupportTicketColumns } from "src/core/shared/components/tables";
import { useDrop } from "src/core/drops/hooks";
import { useCurrentUser } from "src/core/users/hooks";
import { SupportTicketActionList } from "src/modules/support/components/SupportTicketActionList";
import { IStore } from "src/core/shared/store";
import { Table } from "src/core/dashboard/components/widgets";
import { CreateSupportTicketContext } from "src/modules/support/contexts/CreateSupportTicketContext";
import { LikeToDo } from "src/library/components/LikeToDo";
import { IDrop } from "src/core/drops/api/drops.api";

/**
 * Support Tickets overview page
 * Renders support tickets by branch
 */

const defaultPagination: PaginatedParams<ISupportTicket> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "priority", "DESC" ] ]
};

const ViewCustomerSupportTicketsPage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  const TableRef = React.useRef<ITableInstance<ISupportTicket>>(null);
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ supportTickets, setSupportTickets ] = React.useState<PaginatedData<ISupportTicket>>();
  const [ listSupportTicketsRes, listSupportTicketsReq ] = useSupportApi("LIST_BRANCH_TICKETS");
  const [ listUASupportTicketsRes, listUASupportTicketsReq ] = useSupportApi("LIST_USER_ACCESSIBLE_TICKETS");
  const [ { drop } ] = useDrop();
  const [ { currentRole } ] = useCurrentUser();
  const { supportTicketTypes } = useMappedState((store: IStore) => ({ supportTicketTypes: store.global.settings && store.global.settings.supportTicketTypes }));
  const isCustomerAdmin = React.useMemo(() => currentRole?.code === "customer-admin", [ currentRole?.code ]);

  React.useEffect(() => {
    setIsLoading(listSupportTicketsRes.pending || listUASupportTicketsRes.pending);
  }, [ listSupportTicketsRes.pending, listUASupportTicketsRes.pending ]);

  const onFetchData = React.useCallback((params: {filters: FilterParams<ISupportTicket>, pagination: PaginatedParams<ISupportTicket>}) => {
    // Set loading to true

    if (isCustomerAdmin) {
      listUASupportTicketsReq({
        pagination: params.pagination,
        filters: params.filters
      });
    } else if (drop && drop.id) {
    // Fetch support tickets
      listSupportTicketsReq({
        branchId: drop.id,
        pagination: params.pagination,
        filters: params.filters
      });
    }
  }, [
    isCustomerAdmin,
    drop,
    listUASupportTicketsReq,
    listSupportTicketsReq
  ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (listUASupportTicketsRes.error) {
      toast.error(listUASupportTicketsRes.error.message);
    }

    // On success
    if (listUASupportTicketsRes.data) {
      setSupportTickets(listUASupportTicketsRes.data);
    }
  }, [ listUASupportTicketsRes ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (listSupportTicketsRes.error) {
      toast.error(listSupportTicketsRes.error.message);
    }

    // On success
    if (listSupportTicketsRes.data) {
      setSupportTickets(listSupportTicketsRes.data);
    }
  }, [ listSupportTicketsRes ]);

  const columns = React.useMemo(() => {
    return getCustomerSupportTicketColumns({
      actionButton: info => {
        return (
          <SupportTicketActionList
            ticketId={info.row.original.id}
            context={drop?.id && !isCustomerAdmin ? "drop" : undefined}
          />
        );
      },
      terminology: props.terminology,
      currentRole,
      supportTicketTypes,
      ExcludedColumns: !isCustomerAdmin ? [ "branch", "customer" ] : undefined

    });
  }, [
    currentRole,
    drop?.id,
    isCustomerAdmin,
    props.terminology,
    supportTicketTypes
  ]);

  return (
    <Layout
      title={title + " Tickets"}
      {...props}
    >
      <MainContentWrapper fill>

        <LikeToDo
          copy ="What would you like to do?"
          buttons={[
            <ButtonLabelContainer key="request-ticket">
              <ButtonWithLabel direction="vertical">

                <CreateSupportTicketContext
                  createSupportTicket={(dropOverride: IDrop | null) => props.history.push(`tickets/raise-ticket/${dropOverride?.id}`, { cleanStart: true })}
                  drop={isCustomerAdmin ? null : drop}
                  key="create-support-ticket"
                />

              </ButtonWithLabel>
            </ButtonLabelContainer>
          ]}
        />

        <Table
          ref={TableRef}
          loading={isLoading}
          tableOf="Support Tickets"
          defaultPagination={defaultPagination}
          data={supportTickets ? supportTickets.result : []}
          onFetchData={onFetchData}
          pageCount={supportTickets ? supportTickets.pages : 0}
          rowCount={supportTickets ? supportTickets.count : 0}
          columns={columns}
        />
      </MainContentWrapper>
    </Layout>
  );
};

const ButtonLabelContainer = styled(Grid.Bounds)`
  justify-content: flex-start;
`;

const ButtonWithLabel = styled(Grid.Bounds)`
  align-items: center;
  justify-content: center;
  
  ${({ theme }) => css`
    .button-label {
      margin-bottom:  ${theme.spacing.margins.sm};
      align-self: center;
    }
  `}
`;

export default ViewCustomerSupportTicketsPage;
